import { Package, Trash2 } from "lucide-react";
import Button from "../../../../../components/EcommerceActionButton/Button";
import { Label } from "../../../../../components/Label";
import { formatNumberToPrice } from "../../../../../helpers/priceFormatting";
import styles from "../QuoteForm.module.scss";

interface QuoteProductsSectionProps {
  products: QuoteProduct[];
  onAddProduct: () => void;
  onRemoveProduct: (id: string) => void;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onUpdateDiscount: (id: string, discount: number) => void;
  onUpdateMeasure: (id: string, value: number, unit: "m2" | "sheets") => void;
  getDiscountedTotal: (product: QuoteProduct) => number;
  getAccessoryNeeds: (product: QuoteProduct) => {
    adhesiveQty: number;
    sealantQty: number;
  };
  maxDiscountPercent: number;
}

export function QuoteProductsSection({
  products,
  onAddProduct,
  onRemoveProduct,
  onUpdateQuantity,
  onUpdateDiscount,
  onUpdateMeasure,
  getDiscountedTotal,
  getAccessoryNeeds,
  maxDiscountPercent,
}: QuoteProductsSectionProps) {
  return (
    <div className={styles.itemsSection}>
      <div className={styles.itemsHeader}>
        <Label className={styles.label}>Productos</Label>
        <Button
          color="primary"
          shape="little-rounded"
          icon={Package}
          onClick={onAddProduct}
          className={styles.compactButton}
        >
          Agregar Productos
        </Button>
      </div>
      <div className={styles.itemsList}>
        {products.map((product) => {
          const { adhesiveQty, sealantQty } = getAccessoryNeeds(product);
          const inputValue = product.sheetsNeeded || product.quantity || 1;

          return (
            <div key={product.id} className={styles.itemCard}>
              <div className={styles.itemHeader}>
                <div className={styles.itemInfo}>
                  <div className={styles.itemName}>{product.name}</div>
                  {product.requestedM2 && (
                    <div className={styles.itemDetails}>
                      {product.requestedM2} m² • {product.sheetsNeeded} láminas
                    </div>
                  )}
                  {adhesiveQty > 0 && (
                    <div className={styles.itemDetails}>
                      Adhesivos requeridos: {adhesiveQty} unidades
                    </div>
                  )}
                  {sealantQty > 0 && (
                    <div className={styles.itemDetails}>
                      Sellantes requeridos: {sealantQty} unidades
                    </div>
                  )}
                  <div className={styles.itemPrice}>
                    {formatNumberToPrice(product.price)} × {product.quantity}{" "}
                    {product.quantity === 1 ? "lámina" : "láminas"}
                  </div>
                  <div className={styles.quantityRow}>
                    <span className={styles.quantityLabel}>
                      Cantidad (láminas)
                    </span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={inputValue}
                      onChange={(e) =>
                        onUpdateMeasure(
                          product.id,
                          parseInt(e.target.value, 10) || 1,
                          "sheets"
                        )
                      }
                      className={styles.quantityInput}
                    />
                  </div>
                  <div className={styles.discountRow}>
                    <span className={styles.discountLabel}>Descuento %</span>
                    <input
                      type="number"
                      min="0"
                      max={maxDiscountPercent}
                      value={product.discountPercent ?? 0}
                      onChange={(e) =>
                        onUpdateDiscount(
                          product.id,
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className={styles.discountInput}
                    />
                  </div>
                </div>
                <div className={styles.itemTotal}>
                  {formatNumberToPrice(getDiscountedTotal(product))}
                  {product.discountPercent ? (
                    <span className={styles.itemTotalOriginal}>
                      {formatNumberToPrice(product.price * product.quantity)}
                    </span>
                  ) : null}
                </div>
                <Button
                  color="outlined"
                  shape="little-rounded"
                  size="small"
                  icon={Trash2}
                  onClick={() => onRemoveProduct(product.id)}
                  className={styles.iconButton}
                >
                  Quitar
                </Button>
              </div>
            </div>
          );
        })}
        {products.length === 0 && (
          <p className={styles.emptyMessage}>No hay productos agregados</p>
        )}
      </div>
    </div>
  );
}
